/**
 * useNotificationHandler Hook
 * Handles notification clicks and navigation
 */

import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface NotificationData {
  type: string;
  url?: string;
  familyId?: string;
  messageId?: string;
  [key: string]: unknown;
}

export function useNotificationHandler() {
  const navigate = useNavigate();

  const handleNotificationClick = useCallback((data: NotificationData) => {
    console.log('[useNotificationHandler] Handling notification click:', { type: data.type, url: data.url });

    switch (data.type) {
      case 'family-chat':
        window.dispatchEvent(new CustomEvent('open-family-chat'));
        navigate('/app');
        break;

      case 'vma':
        navigate('/app/vma');
        break;

      case 'expiration':
      case 'inventory':
        navigate('/app/inventory');
        break;

      case 'yearwheel':
        navigate('/app/year-wheel');
        break;

      case 'group-membership-request':
      case 'group-membership-decision':
      case 'group-invitation':
      case 'group-request':
      case 'group-decision':
        navigate('/app/groups/notifications');
        break;

      case 'group-activity':
        if (data.groupId && typeof data.groupId === 'string') {
          navigate(`/app/groups/${data.groupId}`);
        } else if (data.url && typeof data.url === 'string') {
          navigate(data.url);
        } else {
          navigate('/app/groups');
        }
        break;

      default:
        if (data.url && typeof data.url === 'string') {
          navigate(data.url);
        } else {
          navigate('/app');
        }
    }
  }, [navigate]);

  useEffect(() => {
    if ('Notification' in window && 'serviceWorker' in navigator) {
      const handleMessage = (event: MessageEvent) => {
        if (event.data && event.data.type === 'notification-click') {
          const data = event.data.data as NotificationData;
          handleNotificationClick(data);
        }
      };

      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          const pendingAction = sessionStorage.getItem('pendingNotificationAction');
          if (pendingAction) {
            try {
              const data = JSON.parse(pendingAction) as NotificationData;
              sessionStorage.removeItem('pendingNotificationAction');
              handleNotificationClick(data);
            } catch (error) {
              console.error('[useNotificationHandler] Failed to parse pending action:', error);
            }
          }
        }
      };

      navigator.serviceWorker.addEventListener('message', handleMessage);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        navigator.serviceWorker.removeEventListener('message', handleMessage);
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    }

    return undefined;
  }, [handleNotificationClick]);
}
